/**
 * Botones de acciones del usuario sobre una convocatoria.
 *
 * Sprint 8.5. Permite favoritar (♥) y descartar (✘) cada convocatoria.
 * El estado se persiste en backend vía `setOpportunityAction`. Optimistic
 * UI (V2-S6): `onMutate` parchea la cache del Radar al instante para que el
 * botón reaccione sin delay; `onError` revierte con el snapshot previo y
 * `onSettled` reconcilia con el server (refetch tras éxito o error).
 *
 * Diseño visual:
 * - Iconos pequeños, color slate por default; brand-700 / red-600 cuando
 *   están activos.
 * - Tooltips simples vía `title=` (no usamos lib de tooltips para esto).
 * - Disabled durante la mutation para evitar double-clicks.
 *
 * El componente NO renderiza el botón "Crear expediente" — eso lo deja
 * el caller en su layout. Acá solo estos 2 toggles.
 */

import {
  type InfiniteData,
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { track } from "@/lib/analytics";
import { setOpportunityAction, type OpportunitiesPage, type OpportunityDTO } from "@/lib/api";

/** Forma de los datos que cachea el Radar: paginas de `fetchOpportunities` (issue #733). */
type OpportunityListCache = InfiniteData<OpportunitiesPage, number>;

/**
 * Parchea optimistamente un flag (`is_favorited` / `is_dismissed`) de una
 * convocatoria en TODAS las queries del Radar (cualquier combinación de
 * filtros + paginación). Devuelve el snapshot previo para revertir en error.
 */
function patchOpportunityFlag(
  queryClient: QueryClient,
  opportunityId: string,
  patch: Partial<Pick<OpportunityDTO, "is_favorited" | "is_dismissed">>,
) {
  const previous = queryClient.getQueriesData<OpportunityListCache>({
    queryKey: ["opportunities"],
  });
  queryClient.setQueriesData<OpportunityListCache>({ queryKey: ["opportunities"] }, (old) => {
    if (!old?.pages) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.map((it) => (it.id === opportunityId ? { ...it, ...patch } : it)),
      })),
    };
  });
  return previous;
}

export interface OpportunityActionButtonsProps {
  opportunityId: string;
  /** Estado actual (viene del DTO). */
  isFavorited: boolean;
  isDismissed: boolean;
  /** DANE del muni activo — necesario para el endpoint. */
  dane: string;
}

export function OpportunityActionButtons({
  opportunityId,
  isFavorited,
  isDismissed,
  dane,
}: OpportunityActionButtonsProps) {
  const queryClient = useQueryClient();

  const favoriteMutation = useMutation({
    mutationFn: () =>
      setOpportunityAction({
        opportunityId,
        action: "favorite",
        dane,
        apply: !isFavorited,
      }),
    // Optimistic UI: el corazón reacciona al instante. Antes solo
    // invalidábamos en onSuccess → el botón quedaba con delay hasta el
    // refetch. Ahora parcheamos la cache ya, y revertimos si el server falla.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["opportunities"] });
      const previous = patchOpportunityFlag(queryClient, opportunityId, {
        is_favorited: !isFavorited,
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: () => {
      track({
        event_name: "opportunity_favorited",
        // `applied` distingue marcar (true) de quitar (false) la favorita.
        properties: { opportunity_id: opportunityId, applied: !isFavorited },
      });
    },
    // Reconcilia con el server (corre tras éxito Y error). Un (des)favorito
    // puede sacar/entrar la card si el filtro "solo favoritas" está activo.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: () =>
      setOpportunityAction({
        opportunityId,
        action: "dismiss",
        dane,
        apply: !isDismissed,
      }),
    // Optimistic UI: al descartar, la card se atenúa al instante (y el
    // refetch en onSettled la saca si el filtro oculta descartadas).
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["opportunities"] });
      const previous = patchOpportunityFlag(queryClient, opportunityId, {
        is_dismissed: !isDismissed,
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: () => {
      track({
        event_name: "opportunity_dismissed",
        // `applied` distingue descartar (true) de restaurar (false).
        properties: { opportunity_id: opportunityId, applied: !isDismissed },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
    },
  });

  return (
    <div className="flex items-center gap-1.5">
      <IconButton
        active={isFavorited}
        loading={favoriteMutation.isPending}
        onClick={() => favoriteMutation.mutate()}
        title={isFavorited ? "Quitar de favoritas" : "Marcar como favorita"}
        activeClass="text-red-600 hover:text-red-700"
        testid="action-favorite"
      >
        {/* heart — filled cuando active, outline cuando no. */}
        {isFavorited ? (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-4 w-4">
            <path d="M12 21s-7-4.35-9.5-9C1 8.5 3 5 6.5 5c2 0 3.5 1 5 3 1.5-2 3-3 5-3 3.5 0 5.5 3.5 4 7-2.5 4.65-9.5 9-9.5 9z" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            aria-hidden="true"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21s-7-4.35-9.5-9C1 8.5 3 5 6.5 5c2 0 3.5 1 5 3 1.5-2 3-3 5-3 3.5 0 5.5 3.5 4 7-2.5 4.65-9.5 9-9.5 9z"
            />
          </svg>
        )}
      </IconButton>

      <IconButton
        active={isDismissed}
        loading={dismissMutation.isPending}
        onClick={() => dismissMutation.mutate()}
        title={isDismissed ? "Restaurar (deshacer descarte)" : "Descartar"}
        activeClass="text-slate-700 hover:text-slate-900"
        testid="action-dismiss"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
          className="h-4 w-4"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </IconButton>
    </div>
  );
}

function IconButton({
  active,
  loading,
  onClick,
  title,
  activeClass,
  testid,
  children,
}: {
  active: boolean;
  loading: boolean;
  onClick: () => void;
  title: string;
  activeClass: string;
  testid: string;
  children: React.ReactNode;
}) {
  const inactive = "text-slate-400 hover:text-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      title={title}
      aria-label={title}
      aria-pressed={active}
      data-testid={testid}
      data-active={active}
      className={
        "inline-flex h-7 w-7 items-center justify-center rounded-full bg-white transition " +
        "ring-1 ring-slate-200 hover:ring-slate-300 disabled:opacity-50" +
        (active ? activeClass : inactive)
      }
    >
      {children}
    </button>
  );
}
